import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import type { AdminParams, EmailPreview, EmailTemplateInput } from './consoleTypes';

export const useAdminConsoleQuery = <T>(resource: string, params?: AdminParams, enabled = true) =>
  useQuery<T>({
    queryKey: ['admin-console', resource, params],
    queryFn: async ({ signal }) =>
      (await apiClient.get<T>(`/admin/${resource}`, { params, signal })).data,
    enabled,
    staleTime: 15_000,
  });

export const useAdminConsoleMutation = <T = unknown>() => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      resource,
      body,
      method = 'POST',
    }: {
      resource: string;
      body?: unknown;
      method?: 'POST' | 'PUT' | 'DELETE';
    }) => (await apiClient.request<T>({ url: `/admin/${resource}`, method, data: body })).data,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['admin-console'] }),
        client.invalidateQueries({ queryKey: ['admin'] }),
      ]);
    },
  });
};

export const useEmailPreviewQuery = (
  key: string,
  locale: string,
  input: EmailTemplateInput,
  enabled: boolean
) =>
  useQuery({
    queryKey: ['admin-preview', key, locale, input],
    queryFn: async ({ signal }) =>
      (
        await apiClient.post<EmailPreview>(
          `/admin/mail/templates/${key}/${locale}/preview`,
          input,
          { signal }
        )
      ).data,
    enabled,
    retry: false,
    staleTime: Infinity,
  });
